import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { PageHeader } from '../../components/PageHeader';
import { Pill, type PillTone } from '../../components/Pill';
import { Select } from '../../components/Select';
import {
  dashboardApi,
  DashboardApiError,
  type DashboardReconciliationStatus,
} from '../../lib/apiClient';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';

const STATUSES = [
  'late_payment',
  'conflict',
  'mismatch',
  'unsettled',
  'pending',
  'matched',
  'rejected',
  'voided',
  'missing',
] as const satisfies readonly DashboardReconciliationStatus[];

const STATUS_LABELS: Record<DashboardReconciliationStatus, string> = {
  late_payment: 'ชำระล่าช้า',
  conflict: 'ข้อมูลขัดแย้ง',
  mismatch: 'ยอดไม่ตรง',
  unsettled: 'ยังไม่รับชำระ',
  pending: 'รอตรวจสอบ',
  matched: 'ตรงกัน',
  rejected: 'ปฏิเสธ',
  voided: 'ยกเลิก',
  missing: 'ไม่พบข้อมูล',
};

const STATUS_TONES: Record<DashboardReconciliationStatus, PillTone> = {
  late_payment: 'warn',
  conflict: 'danger',
  mismatch: 'danger',
  unsettled: 'warn',
  pending: 'primary',
  matched: 'ok',
  rejected: 'danger',
  voided: 'neutral',
  missing: 'danger',
};

const RUN_TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const SETTLEMENT_IMPORT_MAX_BYTES = 1024 * 1024;

function bangkokDate(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function amount(value: number): string {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 2,
  }).format(value / 100);
}

function signedAmount(value: number): string {
  if (value === 0) return amount(0);
  return `${value > 0 ? '+' : '−'}${amount(Math.abs(value))}`;
}

function dateTime(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('th-TH', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'กรุณาลองใหม่อีกครั้ง';
}

function exportErrorMessage(error: unknown): string {
  if (
    error instanceof DashboardApiError &&
    error.code === 'reconciliation_export_range_too_large'
  ) {
    return 'พบข้อมูลเกิน 5,000 รายการ กรุณาลดช่วงวันที่หรือเลือกสถานะให้แคบลง';
  }
  return errorMessage(error);
}

function newRunKey(): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `dashboard-reconciliation-${random}`.replace(/[^A-Za-z0-9._:-]/g, '-');
}

function newSettlementReconcileKey(): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `dashboard-settlement-reconcile-${random}`.replace(
    /[^A-Za-z0-9._:-]/g,
    '-',
  );
}

function newFileAnomalyResolutionReference(): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `dashboard-file-anomaly-${random}`.replace(/[^A-Za-z0-9._:-]/g, '-');
}

function downloadCsv(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'settlement-reconciliation.csv';
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function ReconciliationView() {
  const user = useSession((state) => state.user);
  const shellBranchId = useUi((state) => state.branchId);
  const queryClient = useQueryClient();
  const canAccess = user?.role === 'owner' || user?.role === 'manager';
  const [branchId, setBranchId] = useState('');
  const [from, setFrom] = useState(bangkokDate);
  const [to, setTo] = useState(bangkokDate);
  const [status, setStatus] = useState<DashboardReconciliationStatus | ''>('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null,
  );
  const [runId, setRunId] = useState<string | null>(null);
  const [settlementFile, setSettlementFile] = useState<File | null>(null);
  const [selectedImportId, setSelectedImportId] = useState<string | null>(null);
  const [selectedAnomalyAlertId, setSelectedAnomalyAlertId] = useState<
    string | null
  >(null);
  const [anomalyReasons, setAnomalyReasons] = useState<Record<string, string>>(
    {},
  );
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const runIdentity = useRef<{ fingerprint: string; key: string } | null>(null);
  const settlementReconcileIdentity = useRef<{
    fingerprint: string;
    key: string;
  } | null>(null);
  const anomalyResolutionIdentity = useRef<
    Record<string, { fingerprint: string; reference: string }>
  >({});

  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: () => dashboardApi.listBranches(),
    enabled: canAccess,
  });
  const branches = branchesQuery.data ?? [];
  const allowedBranchIds = useMemo(
    () =>
      user
        ? visibleBranchIds(
            user,
            branches.map((branch) => branch.id),
          )
        : [],
    [branches, user],
  );
  const visibleBranches = branches.filter((branch) =>
    allowedBranchIds.includes(branch.id),
  );

  useEffect(() => {
    if (branchId && allowedBranchIds.includes(branchId)) return;
    setBranchId(
      (shellBranchId && allowedBranchIds.includes(shellBranchId)
        ? shellBranchId
        : null) ??
        allowedBranchIds[0] ??
        '',
    );
  }, [allowedBranchIds, branchId, shellBranchId]);

  const listQuery = useInfiniteQuery({
    queryKey: ['payment-reconciliation', branchId, from, to, status],
    queryFn: ({ pageParam }) =>
      dashboardApi.listReconciliation({
        branchId,
        from,
        to,
        ...(status ? { status } : {}),
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled:
      canAccess &&
      Boolean(branchId) &&
      Boolean(from) &&
      Boolean(to) &&
      from <= to,
  });
  const payments = listQuery.data?.pages.flatMap((page) => page.payments) ?? [];
  const summary = listQuery.data?.pages[0]?.summary;

  const detailQuery = useQuery({
    queryKey: ['payment-reconciliation-detail', selectedPaymentId],
    queryFn: () => dashboardApi.getReconciliationPayment(selectedPaymentId!),
    enabled: canAccess && Boolean(selectedPaymentId),
  });

  const anomalyListQuery = useInfiniteQuery({
    queryKey: ['settlement-import-anomalies', selectedImportId, branchId],
    queryFn: ({ pageParam }) =>
      dashboardApi.listSettlementImportAnomalies({
        importId: selectedImportId!,
        branchId,
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: canAccess && Boolean(branchId) && Boolean(selectedImportId),
  });
  const anomalies =
    anomalyListQuery.data?.pages.flatMap((page) => page.items) ?? [];

  const anomalyDetailQuery = useQuery({
    queryKey: [
      'settlement-import-anomaly-detail',
      selectedImportId,
      selectedAnomalyAlertId,
      branchId,
    ],
    queryFn: () =>
      dashboardApi.getSettlementImportAnomaly({
        importId: selectedImportId!,
        alertId: selectedAnomalyAlertId!,
        branchId,
      }),
    enabled:
      canAccess &&
      Boolean(branchId) &&
      Boolean(selectedImportId) &&
      Boolean(selectedAnomalyAlertId),
  });

  const runMutation = useMutation({
    mutationFn: () => {
      const fingerprint = `${branchId}:${from}:${to}`;
      if (runIdentity.current?.fingerprint !== fingerprint) {
        runIdentity.current = { fingerprint, key: newRunKey() };
      }
      return dashboardApi.createReconciliationRun({
        branchId,
        from,
        to,
        idempotencyKey: runIdentity.current.key,
      });
    },
    onSuccess: (result) => setRunId(result.runId),
  });

  const runQuery = useQuery({
    queryKey: ['payment-reconciliation-run', runId],
    queryFn: () => dashboardApi.getReconciliationRun(runId!),
    enabled: canAccess && Boolean(runId),
    refetchInterval: (query) =>
      RUN_TERMINAL.has(query.state.data?.status ?? '') ? false : 1_500,
  });

  useEffect(() => {
    if (!runQuery.data?.status || !RUN_TERMINAL.has(runQuery.data.status))
      return;
    runIdentity.current = null;
    if (runQuery.data.status === 'completed') {
      void queryClient.invalidateQueries({
        queryKey: ['payment-reconciliation'],
      });
    }
  }, [queryClient, runQuery.data?.status]);

  const resolveMutation = useMutation({
    mutationFn: ({ alertId, reason }: { alertId: string; reason: string }) =>
      dashboardApi.resolveReconciliationAlert({ alertId, reason }),
    onSuccess: async (_result, variables) => {
      setReasons((current) => ({ ...current, [variables.alertId]: '' }));
      await Promise.all([
        detailQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['payment-reconciliation'] }),
      ]);
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) =>
      dashboardApi.importReconciliationSettlementCsv({ branchId, file }),
    onSuccess: async (result) => {
      setSelectedImportId(result.importId);
      settlementReconcileIdentity.current = null;
      await queryClient.invalidateQueries({
        queryKey: ['payment-reconciliation'],
      });
    },
  });

  const settlementReconcileMutation = useMutation({
    mutationFn: () => {
      if (!selectedImportId) {
        throw new Error('ไม่พบ Import ID ที่จะกระทบยอด');
      }
      const fingerprint = `${selectedImportId}:${branchId}:${from}:${to}`;
      if (settlementReconcileIdentity.current?.fingerprint !== fingerprint) {
        settlementReconcileIdentity.current = {
          fingerprint,
          key: newSettlementReconcileKey(),
        };
      }
      return dashboardApi.reconcileSettlementImport({
        importId: selectedImportId,
        branchId,
        from,
        to,
        idempotencyKey: settlementReconcileIdentity.current.key,
      });
    },
    onSuccess: async () => {
      settlementReconcileIdentity.current = null;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['payment-reconciliation'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['payment-reconciliation-detail'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['settlement-import-anomalies'],
        }),
      ]);
    },
  });

  const anomalyResolveMutation = useMutation({
    mutationFn: ({ alertId, reason }: { alertId: string; reason: string }) => {
      if (!selectedImportId) throw new Error('ไม่พบ Import ID ของ alert');
      const fingerprint = `${selectedImportId}:${branchId}:${alertId}:${reason}`;
      if (
        anomalyResolutionIdentity.current[alertId]?.fingerprint !== fingerprint
      ) {
        anomalyResolutionIdentity.current[alertId] = {
          fingerprint,
          reference: newFileAnomalyResolutionReference(),
        };
      }
      return dashboardApi.resolveSettlementImportAnomaly({
        importId: selectedImportId,
        branchId,
        alertId,
        reason,
        resolutionReference:
          anomalyResolutionIdentity.current[alertId]!.reference,
      });
    },
    onSuccess: async (_result, variables) => {
      delete anomalyResolutionIdentity.current[variables.alertId];
      setAnomalyReasons((current) => ({
        ...current,
        [variables.alertId]: '',
      }));
      await Promise.all([
        anomalyDetailQuery.refetch(),
        queryClient.invalidateQueries({
          queryKey: ['settlement-import-anomalies'],
        }),
        queryClient.invalidateQueries({ queryKey: ['payment-reconciliation'] }),
      ]);
    },
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      dashboardApi.exportReconciliationCsv({
        branchId,
        from,
        to,
        ...(status ? { status } : {}),
      }),
    onSuccess: downloadCsv,
  });

  useEffect(() => {
    setSelectedImportId(null);
    setSelectedAnomalyAlertId(null);
    anomalyResolutionIdentity.current = {};
    settlementReconcileIdentity.current = null;
    importMutation.reset();
    settlementReconcileMutation.reset();
  }, [branchId, from, to]);

  if (!canAccess) {
    return (
      <div className="bo-fade">
        <PageHeader
          title="กระทบยอดชำระ"
          subtitle="ตรวจสอบความตรงกันของรายการชำระเงิน"
        />
        <Card>
          <EmptyState
            title="ไม่มีสิทธิ์ดูข้อมูลกระทบยอด"
            sub="เฉพาะผู้จัดการหรือเจ้าของเท่านั้น"
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <PageHeader
        title="กระทบยอดชำระ"
        subtitle="เทียบข้อมูล Gateway, POS และ settlement ตามวันธุรกิจ Asia/Bangkok"
        actions={
          <>
            <Btn
              onClick={() => exportMutation.mutate()}
              disabled={!branchId || from > to || exportMutation.isPending}
            >
              {exportMutation.isPending ? 'กำลังส่งออก…' : 'ส่งออก CSV'}
            </Btn>
            <Btn
              kind="primary"
              onClick={() => runMutation.mutate()}
              disabled={!branchId || from > to || runMutation.isPending}
            >
              {runMutation.isPending ? 'กำลังเริ่ม…' : 'กระทบยอดใหม่'}
            </Btn>
          </>
        }
      />

      <Card className="mb-6">
        <div className="grid gap-4 md:grid-cols-4">
          <Field label="สาขา">
            <Select
              aria-label="สาขา"
              value={branchId}
              onChange={(event) => {
                setBranchId(event.target.value);
                setSettlementFile(null);
                importMutation.reset();
              }}
            >
              {visibleBranches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.short}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="จากวันที่">
            <Input
              aria-label="จากวันที่"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Field>
          <Field label="ถึงวันที่">
            <Input
              aria-label="ถึงวันที่"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </Field>
          <Field label="สถานะ">
            <Select
              aria-label="สถานะ"
              value={status}
              onChange={(event) =>
                setStatus(
                  event.target.value as DashboardReconciliationStatus | '',
                )
              }
            >
              <option value="">ทุกสถานะ</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {from > to ? (
          <p className="mt-3 text-sm-label text-danger">
            วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด
          </p>
        ) : null}
      </Card>

      <Card title="นำเข้า settlement CSV" className="mb-6">
        <div className="grid items-end gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
          <Field label="ไฟล์ settlement CSV">
            <Input
              key={branchId}
              aria-label="ไฟล์ settlement CSV"
              type="file"
              accept=".csv,text/csv"
              disabled={!branchId || importMutation.isPending}
              onChange={(event) => {
                setSettlementFile(event.target.files?.[0] ?? null);
                importMutation.reset();
              }}
            />
          </Field>
          <Btn
            kind="primary"
            disabled={!branchId || !settlementFile || importMutation.isPending}
            onClick={() => {
              if (settlementFile) importMutation.mutate(settlementFile);
            }}
          >
            {importMutation.isPending ? 'กำลังนำเข้า…' : 'นำเข้า settlement'}
          </Btn>
        </div>
        <p className="mt-2 text-sm-label text-ink-3">
          ขนาดสูงสุด 1 MiB และ 5,000 แถว
          ระบบจะตรวจสอบไฟล์และข้อมูลทั้งหมดอีกครั้ง
        </p>
        {settlementFile ? (
          <p className="mt-2 text-sm-label text-ink-2">
            เลือกแล้ว: {settlementFile.name} (
            {new Intl.NumberFormat('th-TH').format(settlementFile.size)} bytes)
          </p>
        ) : null}
        {settlementFile && settlementFile.size > SETTLEMENT_IMPORT_MAX_BYTES ? (
          <p className="mt-2 text-sm-label text-danger">
            ไฟล์เกิน 1 MiB และระบบจะปฏิเสธการนำเข้า
          </p>
        ) : null}
        {importMutation.isError ? (
          <p role="alert" className="mt-3 text-sm-label text-danger">
            นำเข้า settlement ไม่สำเร็จ: {errorMessage(importMutation.error)}
          </p>
        ) : importMutation.data ? (
          <div
            role="status"
            className="mt-3 rounded-lg border border-line p-3 text-sm-label"
          >
            <div className="font-semibold text-ink">
              {importMutation.data.status === 'replay'
                ? `ไฟล์นี้นำเข้าแล้ว ${importMutation.data.rowCount} แถว`
                : `นำเข้าสำเร็จ ${importMutation.data.rowCount} แถว`}
            </div>
            <div className="mt-1 text-ink-3">
              Import ID: {importMutation.data.importId}
            </div>
            {selectedImportId ? (
              <div className="mt-3">
                <Btn
                  size="sm"
                  kind="primary"
                  disabled={from > to || settlementReconcileMutation.isPending}
                  onClick={() => settlementReconcileMutation.mutate()}
                >
                  {settlementReconcileMutation.isPending
                    ? 'กำลังกระทบยอดไฟล์…'
                    : 'กระทบยอดไฟล์นี้'}
                </Btn>
              </div>
            ) : null}
          </div>
        ) : null}
        {settlementReconcileMutation.isError ? (
          <p role="alert" className="mt-3 text-sm-label text-danger">
            กระทบยอดไฟล์ไม่สำเร็จ:{' '}
            {errorMessage(settlementReconcileMutation.error)}
          </p>
        ) : settlementReconcileMutation.data ? (
          <div
            role="status"
            className="mt-3 rounded-lg border border-line p-3 text-sm-label"
          >
            <div className="font-semibold text-ink">
              {settlementReconcileMutation.data.status === 'replay'
                ? 'กระทบยอดไฟล์เดิมแล้ว'
                : 'กระทบยอดไฟล์สำเร็จ'}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-ink-2">
              <span>
                ตรงกัน {settlementReconcileMutation.data.counters.matched}
              </span>
              <span>
                ยอดไม่ตรง{' '}
                {settlementReconcileMutation.data.counters.amountMismatch}
              </span>
              <span>
                อ้างอิงไม่ตรง{' '}
                {settlementReconcileMutation.data.counters.referenceMismatch}
              </span>
              <span>
                ซ้ำ {settlementReconcileMutation.data.counters.duplicate}
              </span>
              <span>
                ไม่มีคู่ {settlementReconcileMutation.data.counters.orphan}
              </span>
              <span>
                ขาด settlement{' '}
                {settlementReconcileMutation.data.counters.missingSettlement}
              </span>
              <span>
                Alert {settlementReconcileMutation.data.counters.alerts}
              </span>
            </div>
          </div>
        ) : null}
        {selectedImportId ? (
          <div className="mt-5 border-t border-line pt-4">
            <h3 className="font-bold">ความผิดปกติจากไฟล์นี้</h3>
            {anomalyListQuery.isLoading ? (
              <p className="mt-2 text-ink-3">กำลังโหลด alert จากไฟล์…</p>
            ) : anomalyListQuery.isError ? (
              <div className="mt-2">
                <p className="text-danger">
                  โหลด alert จากไฟล์ไม่สำเร็จ:{' '}
                  {errorMessage(anomalyListQuery.error)}
                </p>
                <Btn
                  className="mt-2"
                  size="sm"
                  onClick={() => void anomalyListQuery.refetch()}
                >
                  ลองใหม่
                </Btn>
              </div>
            ) : anomalies.length === 0 ? (
              <p className="mt-2 text-ink-3">ไม่พบความผิดปกติจากไฟล์นี้</p>
            ) : (
              <div className="mt-3 space-y-2">
                {anomalies.map((anomaly) => (
                  <div
                    key={anomaly.alertId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line p-3 text-sm-label"
                  >
                    <div>
                      <div className="font-semibold">{anomaly.alertType}</div>
                      <div className="mt-1 text-ink-3">
                        Row: {anomaly.settlementRowId ?? 'ไม่มี settlement row'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Pill
                        tone={anomaly.status === 'resolved' ? 'ok' : 'warn'}
                      >
                        {anomaly.status === 'resolved' ? 'ปิดแล้ว' : 'เปิดอยู่'}
                      </Pill>
                      <Btn
                        size="sm"
                        aria-label={`ดู alert ${anomaly.alertId}`}
                        onClick={() =>
                          setSelectedAnomalyAlertId(anomaly.alertId)
                        }
                      >
                        ดูรายละเอียด
                      </Btn>
                    </div>
                  </div>
                ))}
                {anomalyListQuery.hasNextPage ? (
                  <div className="flex justify-center">
                    <Btn
                      size="sm"
                      disabled={anomalyListQuery.isFetchingNextPage}
                      onClick={() => void anomalyListQuery.fetchNextPage()}
                    >
                      {anomalyListQuery.isFetchingNextPage
                        ? 'กำลังโหลด…'
                        : 'โหลด alert เพิ่ม'}
                    </Btn>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </Card>

      {exportMutation.isError ? (
        <Card className="mb-6">
          <p role="alert" className="text-danger">
            ส่งออก CSV ไม่สำเร็จ: {exportErrorMessage(exportMutation.error)}
          </p>
        </Card>
      ) : null}

      {runMutation.isError ? (
        <Card className="mb-6">
          <p className="text-danger">
            เริ่มกระทบยอดไม่สำเร็จ: {errorMessage(runMutation.error)}
          </p>
        </Card>
      ) : runId ? (
        <Card title="สถานะการกระทบยอด" className="mb-6">
          {runQuery.isLoading ? (
            <p className="text-ink-3">กำลังตรวจสอบสถานะ…</p>
          ) : runQuery.isError ? (
            <p className="text-danger">
              โหลดสถานะไม่สำเร็จ: {errorMessage(runQuery.error)}
            </p>
          ) : runQuery.data ? (
            <div className="flex flex-wrap items-center gap-4 text-body">
              <Pill
                tone={
                  runQuery.data.status === 'completed'
                    ? 'ok'
                    : runQuery.data.status === 'failed'
                      ? 'danger'
                      : 'primary'
                }
              >
                {runQuery.data.status === 'completed'
                  ? 'กระทบยอดเสร็จแล้ว'
                  : runQuery.data.status === 'failed'
                    ? 'กระทบยอดไม่สำเร็จ'
                    : 'กำลังประมวลผล'}
              </Pill>
              <span>ตรวจแล้ว {runQuery.data.processedCount}</span>
              <span>ตรงกัน {runQuery.data.matchedCount}</span>
              <span>Alert {runQuery.data.alertCount}</span>
              <span>ยังไม่ยืนยัน {runQuery.data.unverifiedCount}</span>
              {runQuery.data.errorCode ? (
                <span className="text-danger">
                  รหัสข้อผิดพลาด: {runQuery.data.errorCode}
                </span>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : null}

      {summary ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Card>
            <div className="text-sm-label text-ink-3">รายการทั้งหมด</div>
            <div className="text-h2 font-bold">{summary.total}</div>
          </Card>
          {(['matched', 'pending', 'mismatch', 'conflict'] as const).map(
            (value) => (
              <Card key={value}>
                <div className="text-sm-label text-ink-3">
                  {STATUS_LABELS[value]}
                </div>
                <div className="text-h2 font-bold">{summary[value]}</div>
              </Card>
            ),
          )}
        </div>
      ) : null}

      {branchesQuery.isLoading || listQuery.isLoading ? (
        <Card>
          <p className="text-ink-3">กำลังโหลดข้อมูลกระทบยอด…</p>
        </Card>
      ) : branchesQuery.isError ? (
        <Card>
          <EmptyState
            title="โหลดข้อมูลสาขาไม่สำเร็จ"
            sub={errorMessage(branchesQuery.error)}
          />
        </Card>
      ) : visibleBranches.length === 0 ? (
        <Card>
          <EmptyState
            title="ไม่พบสาขาที่ได้รับสิทธิ์"
            sub="ติดต่อเจ้าของระบบเพื่อตรวจสอบขอบเขตสาขา"
          />
        </Card>
      ) : listQuery.isError ? (
        <Card>
          <EmptyState
            title="โหลดข้อมูลกระทบยอดไม่สำเร็จ"
            sub={errorMessage(listQuery.error)}
            action={<Btn onClick={() => void listQuery.refetch()}>ลองใหม่</Btn>}
          />
        </Card>
      ) : payments.length === 0 ? (
        <Card>
          <EmptyState
            title="ไม่พบรายการที่ต้องกระทบยอด"
            sub="ลองเปลี่ยนช่วงวันที่หรือสถานะ"
          />
        </Card>
      ) : (
        <>
          <div className="bo-card overflow-x-auto">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>Payment</th>
                  <th>เวลา</th>
                  <th>ยอด</th>
                  <th>Gateway / POS</th>
                  <th>สถานะ</th>
                  <th>Alert</th>
                  <th aria-label="การทำงาน" />
                </tr>
              </thead>
              <tbody>
                {payments.map((item) => (
                  <tr key={item.paymentId}>
                    <td>
                      <div className="font-semibold">{item.paymentId}</div>
                      <div className="text-sm-label text-ink-3">
                        {item.saleId ?? 'ไม่มี Sale ID'}
                      </div>
                    </td>
                    <td>{dateTime(item.createdAt)}</td>
                    <td>{amount(item.amountSatang)}</td>
                    <td>
                      {item.gatewayTruth ?? '—'} / {item.projectionTruth ?? '—'}
                    </td>
                    <td>
                      <Pill tone={STATUS_TONES[item.status]}>
                        {STATUS_LABELS[item.status]}
                      </Pill>
                    </td>
                    <td>{item.openAlertCount}</td>
                    <td>
                      <Btn
                        size="sm"
                        onClick={() => setSelectedPaymentId(item.paymentId)}
                        aria-label={`ดู ${item.paymentId}`}
                      >
                        ดูรายละเอียด
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {listQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Btn
                onClick={() => void listQuery.fetchNextPage()}
                disabled={listQuery.isFetchingNextPage}
              >
                {listQuery.isFetchingNextPage ? 'กำลังโหลด…' : 'โหลดเพิ่ม'}
              </Btn>
            </div>
          ) : null}
        </>
      )}

      <Modal
        open={Boolean(selectedPaymentId)}
        onClose={() => setSelectedPaymentId(null)}
        title="รายละเอียดการชำระ"
        className="w-[min(760px,94vw)]"
      >
        {detailQuery.isLoading ? (
          <p className="text-ink-3">กำลังโหลดรายละเอียด…</p>
        ) : detailQuery.isError ? (
          <EmptyState
            title="โหลดรายละเอียดไม่สำเร็จ"
            sub={errorMessage(detailQuery.error)}
          />
        ) : detailQuery.data ? (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="text-sm-label text-ink-3">Payment ID</div>
                <div className="font-semibold break-all">
                  {detailQuery.data.paymentId}
                </div>
              </div>
              <div>
                <div className="text-sm-label text-ink-3">Sale ID</div>
                <div>{detailQuery.data.saleId ?? '—'}</div>
              </div>
              <div>
                <div className="text-sm-label text-ink-3">ยอดชำระ</div>
                <div>{amount(detailQuery.data.amountSatang)}</div>
              </div>
              <div>
                <div className="text-sm-label text-ink-3">อัปเดตล่าสุด</div>
                <div>{dateTime(detailQuery.data.updatedAt)}</div>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-2">Alerts</h4>
              {detailQuery.data.alerts.length === 0 ? (
                <p className="text-ink-3">ไม่มี alert</p>
              ) : (
                <div className="space-y-3">
                  {detailQuery.data.alerts.map((alert, index) => {
                    const id = alert.alertId ?? `unknown-${index}`;
                    const open = Boolean(alert.alertId && !alert.resolvedAt);
                    return (
                      <div
                        key={id}
                        className="rounded-lg border border-line p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold">
                            {alert.type ?? 'unknown'}
                          </span>
                          <Pill
                            tone={
                              alert.resolvedAt
                                ? 'ok'
                                : alert.severity === 'critical'
                                  ? 'danger'
                                  : 'warn'
                            }
                          >
                            {alert.resolvedAt ? 'ปิดแล้ว' : 'เปิดอยู่'}
                          </Pill>
                        </div>
                        {open ? (
                          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                            <Input
                              aria-label={`เหตุผลการปิด alert ${alert.alertId}`}
                              maxLength={500}
                              value={reasons[id] ?? ''}
                              onChange={(event) =>
                                setReasons((current) => ({
                                  ...current,
                                  [id]: event.target.value,
                                }))
                              }
                              placeholder="ระบุเหตุผลที่ตรวจสอบได้"
                            />
                            <Btn
                              size="sm"
                              kind="primary"
                              disabled={
                                !reasons[id]?.trim() ||
                                resolveMutation.isPending
                              }
                              onClick={() =>
                                resolveMutation.mutate({
                                  alertId: alert.alertId!,
                                  reason: reasons[id]!.trim(),
                                })
                              }
                            >
                              ปิด alert
                            </Btn>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
              {resolveMutation.isError ? (
                <p className="mt-2 text-sm-label text-danger">
                  ปิด alert ไม่สำเร็จ: {errorMessage(resolveMutation.error)}
                </p>
              ) : null}
            </div>
            <div>
              <h4 className="font-bold mb-2">
                Attempts ({detailQuery.data.attempts.length})
              </h4>
              {detailQuery.data.attempts.length === 0 ? (
                <p className="text-ink-3">ไม่มี attempt</p>
              ) : (
                <div className="space-y-2">
                  {detailQuery.data.attempts.map((attempt, index) => (
                    <div
                      key={attempt.attemptId ?? index}
                      className="rounded-lg border border-line p-3 text-sm-label"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="font-semibold">
                          {attempt.attemptId ?? 'ไม่ทราบ Attempt ID'}
                        </span>
                        <Pill tone="primary">
                          {attempt.status ?? 'unknown'}
                        </Pill>
                      </div>
                      <div className="mt-1 text-ink-2">
                        Provider transaction:{' '}
                        {attempt.providerTransactionId ?? '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        Reference:{' '}
                        {attempt.references?.reconciliation ??
                          attempt.references?.primary ??
                          attempt.references?.routing ??
                          '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        อัปเดต: {dateTime(attempt.updatedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-bold mb-2">
                Events ({detailQuery.data.events.length})
              </h4>
              {detailQuery.data.events.length === 0 ? (
                <p className="text-ink-3">ไม่มี event</p>
              ) : (
                <div className="space-y-2">
                  {detailQuery.data.events.map((event, index) => (
                    <div
                      key={event.eventId ?? index}
                      className="rounded-lg border border-line p-3 text-sm-label"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="font-semibold">
                          {event.source ?? 'unknown source'}
                        </span>
                        <span>
                          {event.processingResult ?? 'unknown result'}
                        </span>
                      </div>
                      <div className="mt-1 text-ink-2">
                        Validation: {event.validationResult ?? '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        เกิดเมื่อ:{' '}
                        {dateTime(event.occurredAt ?? event.receivedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-bold mb-2">
                Settlements ({detailQuery.data.settlements.length})
              </h4>
              {detailQuery.data.settlements.length === 0 ? (
                <p className="text-ink-3">ไม่มี settlement</p>
              ) : (
                <div className="space-y-2">
                  {detailQuery.data.settlements.map((settlement, index) => (
                    <div
                      key={settlement.matchId ?? index}
                      className="rounded-lg border border-line p-3 text-sm-label"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="font-semibold">
                          {settlement.outcome ?? 'unknown outcome'}
                        </span>
                        <span>{amount(settlement.settlementAmountSatang)}</span>
                      </div>
                      <div className="mt-1 text-ink-2">
                        Provider transaction:{' '}
                        {settlement.providerTransactionId ?? '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        รับชำระ:{' '}
                        {dateTime(settlement.settledAt ?? settlement.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(selectedImportId && selectedAnomalyAlertId)}
        onClose={() => setSelectedAnomalyAlertId(null)}
        title="รายละเอียดความผิดปกติของไฟล์"
        className="w-[min(680px,94vw)]"
      >
        {anomalyDetailQuery.isLoading ? (
          <p className="text-ink-3">กำลังโหลดรายละเอียด…</p>
        ) : anomalyDetailQuery.isError ? (
          <EmptyState
            title="โหลดรายละเอียด alert ไม่สำเร็จ"
            sub={errorMessage(anomalyDetailQuery.error)}
          />
        ) : anomalyDetailQuery.data ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <span className="font-bold">
                {anomalyDetailQuery.data.alert.alertType}
              </span>
              <Pill
                tone={
                  anomalyDetailQuery.data.alert.status === 'resolved'
                    ? 'ok'
                    : 'warn'
                }
              >
                {anomalyDetailQuery.data.alert.status === 'resolved'
                  ? 'ปิดแล้ว'
                  : 'เปิดอยู่'}
              </Pill>
            </div>
            {anomalyDetailQuery.data.settlementRow ? (
              <div className="grid gap-3 rounded-lg border border-line p-3 text-sm-label sm:grid-cols-2">
                <div>
                  <div className="text-ink-3">Settlement Row ID</div>
                  <div className="break-all font-semibold">
                    {anomalyDetailQuery.data.settlementRow.settlementRowId}
                  </div>
                </div>
                <div>
                  <div className="text-ink-3">Provider transaction ID</div>
                  <div>
                    {anomalyDetailQuery.data.settlementRow
                      .providerTransactionIdPresent
                      ? 'มีข้อมูล'
                      : 'ไม่มีข้อมูล'}
                  </div>
                </div>
                <div>
                  <div className="text-ink-3">Reference ที่มีในไฟล์</div>
                  <div>
                    {[
                      anomalyDetailQuery.data.settlementRow.referencePresence
                        .primary
                        ? 'primary'
                        : null,
                      anomalyDetailQuery.data.settlementRow.referencePresence
                        .reconciliation
                        ? 'reconciliation'
                        : null,
                      anomalyDetailQuery.data.settlementRow.referencePresence
                        .routing
                        ? 'routing'
                        : null,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'ไม่มีข้อมูล'}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-ink-3">Alert นี้ไม่มี settlement row</p>
            )}
            {anomalyDetailQuery.data.match ? (
              <div className="space-y-3">
                <div className="grid gap-3 rounded-lg border border-line p-3 text-sm-label sm:grid-cols-2">
                  <div>
                    <div className="text-ink-3">Match outcome</div>
                    <div className="font-semibold">
                      {anomalyDetailQuery.data.match.outcome}
                    </div>
                  </div>
                  <div>
                    <div className="text-ink-3">ยอด settlement</div>
                    <div>
                      {anomalyDetailQuery.data.settlementRow
                        ? `${amount(anomalyDetailQuery.data.settlementRow.amountSatang)} ${anomalyDetailQuery.data.settlementRow.currency}`
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-ink-3">ยอดที่คาดหมาย</div>
                    <div>
                      {anomalyDetailQuery.data.match.expectedAmountSatang ===
                      null
                        ? '—'
                        : `${amount(anomalyDetailQuery.data.match.expectedAmountSatang)} ${anomalyDetailQuery.data.match.currency ?? '—'}`}
                    </div>
                  </div>
                  <div>
                    <div className="text-ink-3">ผลต่าง</div>
                    <div className="font-semibold tabular-nums">
                      {anomalyDetailQuery.data.settlementRow &&
                      anomalyDetailQuery.data.match.expectedAmountSatang !==
                        null
                        ? `${signedAmount(
                            anomalyDetailQuery.data.settlementRow.amountSatang -
                              anomalyDetailQuery.data.match
                                .expectedAmountSatang,
                          )} ${anomalyDetailQuery.data.settlementRow.currency}`
                        : '—'}
                    </div>
                  </div>
                </div>
                <h4 className="font-bold">สรุป payment candidate</h4>
                <p className="mt-2 text-sm-label text-ink-3">
                  พบ {anomalyDetailQuery.data.match.candidateEvidence.total}{' '}
                  รายการ
                  {anomalyDetailQuery.data.match.candidateEvidence.truncated
                    ? ' (จำนวนถูกจำกัดในรายละเอียด)'
                    : ''}
                </p>
              </div>
            ) : null}
            {anomalyDetailQuery.data.alert.status === 'open' ? (
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  aria-label={`เหตุผลการปิด file alert ${anomalyDetailQuery.data.alert.alertId}`}
                  maxLength={500}
                  value={
                    anomalyReasons[anomalyDetailQuery.data.alert.alertId] ?? ''
                  }
                  onChange={(event) =>
                    setAnomalyReasons((current) => ({
                      ...current,
                      [anomalyDetailQuery.data!.alert.alertId]:
                        event.target.value,
                    }))
                  }
                  placeholder="ระบุเหตุผลที่ตรวจสอบได้"
                />
                <Btn
                  size="sm"
                  kind="primary"
                  disabled={
                    !anomalyReasons[
                      anomalyDetailQuery.data.alert.alertId
                    ]?.trim() || anomalyResolveMutation.isPending
                  }
                  onClick={() =>
                    anomalyResolveMutation.mutate({
                      alertId: anomalyDetailQuery.data!.alert.alertId,
                      reason:
                        anomalyReasons[
                          anomalyDetailQuery.data!.alert.alertId
                        ]!.trim(),
                    })
                  }
                >
                  ปิด alert
                </Btn>
              </div>
            ) : null}
            {anomalyResolveMutation.isError ? (
              <p className="text-danger">
                ปิด alert ไม่สำเร็จ:{' '}
                {errorMessage(anomalyResolveMutation.error)}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
